import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MatOption } from '@angular/material/core';
import { CustomDateAdapter } from '@app/core/adapter/custom-date-adapter';
import { DeviceService } from '@app/core/services/device.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { MatFormField, MatPrefix, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatDatepickerInput, MatDatepickerToggleIcon, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';

@UntilDestroy()
@Component({
  selector:    'pezi-text',
  templateUrl: './pezi-text.component.html',
  styleUrls:   ['./pezi-text.component.scss'],
  providers:   [
    {
      provide:     NG_VALUE_ACCESSOR,
      multi:       true,
      useExisting: forwardRef(() => PeziTextComponent)
    },
    {
      provide:  DateAdapter,
      useClass: CustomDateAdapter
    }
  ],
  standalone:  true,
  imports:     [
    NgIf,
    NgClass,
    MatFormField,
    ReactiveFormsModule,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    NgFor,
    MatOption,
    MatIcon,
    MatPrefix,
    MatError,
    MatDatepickerInput,
    MatDatepickerToggleIcon,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker
  ]
})
export class PeziTextComponent implements ControlValueAccessor {

  @ViewChild('inputEl', {static: false}) input: ElementRef;

  @Input()
  formControlName: string;

  @Input()
  label: string;

  @Input()
  set formGroup(fg: FormGroup) {
    this._formGroup = fg;
    if (fg) {
      this.initSuggestionFilterSub();
    }
  }

  get formGroup(): FormGroup {
    return this._formGroup;
  }

  // Optionale Felder
  @Input()
  maxLen = 200;

  @Input()
  errorText: string;

  @Input()
  type = 'text';

  @Input()
  leadingIcon: string;

  @Input()
  hideHelperText = false;

  @Input()
  useDefaultSuggestionFilter = true;

  @Input()
  set suggestions(values: string[]) {
    this._suggestions = values;
    this.filteredSuggestions = values;
  }

  get suggestions(): string[] {
    return this._suggestions;
  }

  // This not used for Validation! Only for the browser to limit input values which is not 100% safe.
  // Those values can still be out of range!
  // Use custom validator for further handling e.g. Datevalidator
  @Input()
  min: string;

  @Input()
  max: string;

  @Input()
  handyStyle = false;

  @Input()
  placeholder: string;

  @Output()
  public onFocusEvent = new EventEmitter();

  @Output()
  public onSuggestionClick = new EventEmitter<number>();

  @Output()
  public onLeadingIconClick = new EventEmitter<void>();

  public filteredSuggestions: string[];
  public currentFocus = false;
  private _formGroup: FormGroup;
  private _suggestions: string[];
  private suggestionFilterSub: Subscription;

  constructor(private eRef: ElementRef, private cd: ChangeDetectorRef, public deviceSevice: DeviceService) { }





  public getErrorText(): string {
    if (this.errorText) {
      return this.errorText;
    } else {
      if (this.formGroup.controls[this.formControlName].getError('invalidDate')) {
        return 'Datum zu groß/klein';
      }
    }
  }

  public focusText(): void {
    if (this.input && this.input.nativeElement) {
      this.input.nativeElement.focus();
    }
  }

  public displaySuggestions(focus: boolean): void {
    this.currentFocus = focus;
    if (focus) {
      this.onFocusEvent.emit();
    }
  }

  public applySuggestion(index: number, suggestion: string): void {
    this.onSuggestionClick.emit(index);
  }

  clearDate(event: Event) {
    event.stopPropagation();
    this.formGroup.controls[this.formControlName].setValue(null);
  }

  registerOnChange(fn: any): void {
    // This is intentional
  }

  registerOnTouched(fn: any): void {
    // This is intentional
  }

  writeValue(obj: any): void {
    // This is intentional
  }

  private initSuggestionFilterSub(): void {
    if (!this.suggestionFilterSub && this.formControlName && this.useDefaultSuggestionFilter) {
      this.suggestionFilterSub = this.formGroup.valueChanges.pipe(untilDestroyed(this))
        .subscribe(v => {
          if (v && this.suggestions && this.suggestions.length > 0) {
            if (v[this.formControlName]) {
              this.filteredSuggestions = this.suggestions.filter(sug => sug.toLowerCase()
                .indexOf(v[this.formControlName].toLowerCase()) !== -1);
            } else {
              this.filteredSuggestions = this.suggestions;
            }
          }
        });
    }
  }
}
